import { useState, useEffect, useRef, useCallback, createContext, useContext } from "react";
import bg from "./bg.mp4";
import { uploadCV, pushToFirebase } from "./firebase";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";

const stripePromise = loadStripe("pk_live_51SFwWTPOFmqWWWW9pw8jcBzJrqI0bW6YxsHb8V22DkPLY8eNOfEjB1NzlRobrwjQMt0AwPFwnEZ3lepBjmeUbK2V00r41Wg7Ky");
const BACKEND_URL = "https://backendpayment-production.up.railway.app/api/payments/create-intent/";

const CONTACT_EMAIL = "info@dhsociety.de";
const VENUE = "Space Shack, Berlin";

// ── Coupons ───────────────────────────────────────────────────────
// code -> percent discount off the workshop price
const COUPONS = {
  "A-Max70DOBqe": 70,
  "A-Max50D": 50,
  "AM-DHS90DOBqsa108964": 90,
  "AM-DHS95DOBqsa11203": 95,
};

// ── Language setup ───────────────────────────────────────────────
const LANGS = ["en", "de", "tr", "ru"];
const LANG_LABEL = { en: "EN", de: "DE", tr: "TR", ru: "RU" };
const LangContext = createContext("en");
const useLang = () => useContext(LangContext);

// ── Workshop domains (unchanged — all four domains are always kept,
// only Aerospace is currently bookable) ─────────────────────────
const WORKSHOPS = [
  {
    id: "DHS-AE",
    titleKey: "Viator Navigation",
    location: VENUE,
    dates: { day1: "21 August 2026" },
    price: 450,
    totalSeats: 10,
    seatsLeft: 13,
    duration: "6-Hour Intensive",
    level: "Advanced",
    imageUrl: "https://images.unsplash.com/photo-1517976487492-5750f3195933?w=80&h=80&fit=crop&crop=center",
    color: "#c8f03c",
    enabled: true,
  },
  {
    id: "DHS-DF",
    titleKey: "Defence Tech",
    location: null,
    dates: { day1: "TBD" },
    price: 450,
    totalSeats: 10,
    seatsLeft: 20,
    duration: "6-Hour Intensive",
    level: "Advanced",
    imageUrl: "https://images.unsplash.com/photo-1581092921461-eab10380ed66?w=80&h=80&fit=crop&crop=center",
    color: "#ff2bd6",
    enabled: false,
  },
  {
    id: "DHS-ES",
    titleKey: "Embedded Systems",
    location: null,
    dates: { day1: "TBD" },
    price: 500,
    totalSeats: 10,
    seatsLeft: 20,
    duration: "6-Hour Intensive",
    level: "Advanced",
    imageUrl: "https://images.unsplash.com/photo-1518770660439-4636190af475?w=80&h=80&fit=crop&crop=center",
    color: "#00f0ff",
    enabled: false,
  },
  {
  id: "DHS-BT",
  titleKey: "Biotech",
  location: null,
  dates: { day1: "TBD" },
  price: 600,
  totalSeats: 10,
  seatsLeft: 20,
  duration: "6-Hour Intensive",
  level: "Advanced",
  imageUrl: "https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?w=80&h=80&fit=crop&crop=center",
  color: "#34d399",
  enabled: false,
},
{
  id: "DHS-NT",
  titleKey: "Neurotech",
  location: null,
  dates: { day1: "TBD" },
  price: 600,
  totalSeats: 10,
  seatsLeft: 20,
  duration: "6-Hour Intensive",
  level: "Advanced",
  imageUrl: "https://images.unsplash.com/photo-1559757175-5700dde675bc?w=80&h=80&fit=crop&crop=center",
  color: "#a78bfa",
  enabled: false,
},
{
  id: "DHS-QC",
  titleKey: "Quantum Computing",
  location: null,
  dates: { day1: "TBD" },
  price: 700,
  totalSeats: 10,
  seatsLeft: 20,
  duration: "6-Hour Intensive",
  level: "Expert",
  imageUrl: "https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=80&h=80&fit=crop&crop=center",
  color: "#fbbf24",
  enabled: false,
},
{
  id: "DHS-RB",
  titleKey: "Advanced Robotics",
  location: null,
  dates: { day1: "TBD" },
  price: 600,
  totalSeats: 10,
  seatsLeft: 20,
  duration: "6-Hour Intensive",
  level: "Advanced",
  imageUrl: "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=80&h=80&fit=crop&crop=center",
  color: "#60a5fa",
  enabled: false,
},
  {
    id: "DHS-AU",
    titleKey: "Autonomous Systems",
    location: null,
    dates: { day1: "TBD" },
    price: 650,
    totalSeats: 10,
    seatsLeft: 20,
    duration: "6-Hour Intensive",
    level: "Expert",
    imageUrl: "https://images.unsplash.com/photo-1516849841032-87cbac4d88f7?w=80&h=80&fit=crop&crop=center",
    color: "#e879f9",
    enabled: false,
  },
];

// Per-workshop, per-language copy. Every domain (Aerospace, Defence Tech,
// Embedded Systems, Autonomous Systems) is kept and fully translated.
const WS_TEXT = {
  "DHS-AE": {
    en: { title: "Viator One - Aerospace", tagline: "Orbit-class hardware. Built by you.", category: "Aerospace Engineering", description: "Design, build, and flight-test a 3D-printed rocket airframe — from CAD and PCB integration to firmware flash and a live non-explosive flight demonstration.", modules: ["Assembly", "Electronics", "3D Casing Design", "Firmware", "Flight Systems"] },
    de: { title: "Luft- und Raumfahrt", tagline: "Hardware in Orbit-Klasse. Von dir gebaut.", category: "Luft- und Raumfahrttechnik", description: "Entwirf, baue und teste eine 3D-gedruckte Raketenstruktur im Flug — von CAD und Platinenintegration über das Flashen der Firmware bis zur live durchgeführten, nicht-explosiven Flugdemonstration.", modules: ["Montage", "Elektronik", "3D-Gehäusedesign", "Firmware", "Flugsysteme"] },
    tr: { title: "Havacılık ve Uzay", tagline: "Yörünge sınıfı donanım. Senin ellerinle.", category: "Havacılık ve Uzay Mühendisliği", description: "3D baskılı bir roket gövdesi tasarlayın, üretin ve uçuş testine tabi tutun — CAD ve PCB entegrasyonundan firmware yüklemeye ve canlı, patlamasız bir uçuş gösterimine kadar.", modules: ["Montaj", "Elektronik", "3D Kasa Tasarımı", "Firmware", "Uçuş Sistemleri"] },
    ru: { title: "Аэрокосмическая техника", tagline: "Оборудование орбитального класса. Сделано вами.", category: "Аэрокосмическая инженерия", description: "Спроектируйте, соберите и проведите лётные испытания напечатанного на 3D-принтере корпуса ракеты — от CAD и интеграции печатной платы до прошивки firmware и живой невзрывной демонстрации полёта.", modules: ["Сборка", "Электроника", "3D-проектирование корпуса", "Прошивка", "Полётные системы"] },
  },
  "DHS-DF": {
    en: { title: "Defence-inspired embedded systems", tagline: "Secure, high-reliability systems. Live.", category: "Defence-Inspired Embedded Systems", description: "Secure, high-reliability embedded systems engineering inspired by defence-grade architectures — sensor fusion, hardened firmware, and live system integration.", modules: ["Assembly", "Electronics", "3D Casing Design", "Firmware", "Secure Systems"] },
    de: { title: "Verteidigungstechnik", tagline: "Sichere, hochzuverlässige Systeme. Live.", category: "Verteidigungsinspirierte Embedded-Systeme", description: "Sichere, hochzuverlässige Embedded-Systems-Entwicklung nach dem Vorbild von Architekturen im Verteidigungsbereich — Sensorfusion, gehärtete Firmware und Live-Systemintegration.", modules: ["Montage", "Elektronik", "3D-Gehäusedesign", "Firmware", "Sichere Systeme"] },
    tr: { title: "Savunma Teknolojisi", tagline: "Güvenli, yüksek güvenilirlikli sistemler. Canlı.", category: "Savunma Esinli Gömülü Sistemler", description: "Savunma sınıfı mimarilerden esinlenen, güvenli ve yüksek güvenilirlikli gömülü sistem mühendisliği — sensör füzyonu, sertleştirilmiş firmware ve canlı sistem entegrasyonu.", modules: ["Montaj", "Elektronik", "3D Kasa Tasarımı", "Firmware", "Güvenli Sistemler"] },
    ru: { title: "Оборонные технологии", tagline: "Защищённые, отказоустойчивые системы. В прямом эфире.", category: "Встраиваемые системы оборонного типа", description: "Разработка защищённых, высоконадёжных встраиваемых систем по мотивам архитектур оборонного класса — слияние данных с датчиков, защищённая прошивка и живая интеграция системы.", modules: ["Сборка", "Электроника", "3D-проектирование корпуса", "Прошивка", "Защищённые системы"] },
  },
  "DHS-ES": {
    en: { title: "Embedded Systems", tagline: "Bare PCB to production firmware.", category: "Embedded Systems", description: "Bare PCB to production firmware in one session — design, populate, and flash a complete embedded system from scratch.", modules: ["Assembly", "Electronics", "3D Casing Design", "Firmware", "Embedded Systems"] },
    de: { title: "Embedded Systems", tagline: "Von der nackten Platine zur Produktions-Firmware.", category: "Embedded Systems", description: "Von der nackten Platine bis zur produktionsreifen Firmware in einer Session — entwirf, bestücke und flashe ein komplettes Embedded-System von Grund auf.", modules: ["Montage", "Elektronik", "3D-Gehäusedesign", "Firmware", "Embedded Systems"] },
    tr: { title: "Gömülü Sistemler", tagline: "Çıplak PCB'den üretim firmware'ine.", category: "Gömülü Sistemler", description: "Tek bir oturumda çıplak PCB'den üretim firmware'ine — sıfırdan eksiksiz bir gömülü sistem tasarlayın, bileşenlerini yerleştirin ve yükleyin.", modules: ["Montaj", "Elektronik", "3D Kasa Tasarımı", "Firmware", "Gömülü Sistemler"] },
    ru: { title: "Встраиваемые системы", tagline: "От голой платы до серийной прошивки.", category: "Встраиваемые системы", description: "От голой печатной платы до готовой к производству прошивки за одну сессию — спроектируйте, соберите и прошейте полноценную встраиваемую систему с нуля.", modules: ["Сборка", "Электроника", "3D-проектирование корпуса", "Прошивка", "Встраиваемые системы"] },
  },
  "DHS-BT": {
  en: { title: "Biotech", tagline: "Engineer life at the bench.", category: "Biotechnology / Synthetic Biology", description: "Hands-on synthetic biology and biosensor engineering — from PCR and gene-editing basics to building a firmware-linked lab diagnostic device.", modules: ["Assembly", "Electronics", "3D Casing Design", "Firmware", "Biosensor Design"] },
  de: { title: "Biotech", tagline: "Leben am Labortisch gestalten.", category: "Biotechnologie / Synthetische Biologie", description: "Praxisnahe synthetische Biologie und Biosensor-Entwicklung — von PCR und Grundlagen der Genbearbeitung bis zum Bau eines firmware-gesteuerten Labordiagnosegeräts.", modules: ["Montage", "Elektronik", "3D-Gehäusedesign", "Firmware", "Biosensor-Design"] },
  tr: { title: "Biyoteknoloji", tagline: "Hayatı laboratuvar tezgahında tasarla.", category: "Biyoteknoloji / Sentetik Biyoloji", description: "PCR ve gen düzenleme temellerinden firmware bağlantılı bir laboratuvar tanı cihazı inşa etmeye kadar uygulamalı sentetik biyoloji ve biyosensör mühendisliği.", modules: ["Montaj", "Elektronik", "3D Kasa Tasarımı", "Firmware", "Biyosensör Tasarımı"] },
  ru: { title: "Биотехнологии", tagline: "Инженерия жизни за лабораторным столом.", category: "Биотехнологии / Синтетическая биология", description: "Практическая синтетическая биология и разработка биосенсоров — от основ ПЦР и редактирования генов до создания лабораторного диагностического устройства, связанного с прошивкой.", modules: ["Сборка", "Электроника", "3D-проектирование корпуса", "Прошивка", "Разработка биосенсоров"] },
},
"DHS-NT": {
  en: { title: "Neurotech", tagline: "Read signals straight from the brain.", category: "Neurotechnology / Brain-Computer Interfaces", description: "Build and calibrate an EEG-based brain-computer interface — signal acquisition, real-time processing, and a live closed-loop control demonstration.", modules: ["Assembly", "Electronics", "3D Casing Design", "Firmware", "Signal Processing"] },
  de: { title: "Neurotech", tagline: "Signale direkt aus dem Gehirn lesen.", category: "Neurotechnologie / Gehirn-Computer-Schnittstellen", description: "Baue und kalibriere eine EEG-basierte Gehirn-Computer-Schnittstelle — Signalerfassung, Echtzeitverarbeitung und eine live durchgeführte Regelkreis-Demonstration.", modules: ["Montage", "Elektronik", "3D-Gehäusedesign", "Firmware", "Signalverarbeitung"] },
  tr: { title: "Nörotech", tagline: "Sinyalleri doğrudan beyinden oku.", category: "Nöroteknoloji / Beyin-Bilgisayar Arayüzleri", description: "EEG tabanlı bir beyin-bilgisayar arayüzü inşa edin ve kalibre edin — sinyal toplama, gerçek zamanlı işleme ve canlı bir kapalı döngü kontrol gösterimi.", modules: ["Montaj", "Elektronik", "3D Kasa Tasarımı", "Firmware", "Sinyal İşleme"] },
  ru: { title: "Нейротехнологии", tagline: "Считывайте сигналы прямо из мозга.", category: "Нейротехнологии / Интерфейсы мозг-компьютер", description: "Соберите и откалибруйте интерфейс мозг-компьютер на основе ЭЭГ — сбор сигналов, обработка в реальном времени и живая демонстрация управления с обратной связью.", modules: ["Сборка", "Электроника", "3D-проектирование корпуса", "Прошивка", "Обработка сигналов"] },
},
"DHS-QC": {
  en: { title: "Quantum Computing", tagline: "Qubits, gates, and real hardware.", category: "Quantum Computing", description: "Design quantum circuits and run them on real quantum hardware — gate-level logic, error basics, and a live hybrid classical-quantum demonstration.", modules: ["Assembly", "Electronics", "3D Casing Design", "Firmware", "Quantum Circuits"] },
  de: { title: "Quantencomputing", tagline: "Qubits, Gatter und echte Hardware.", category: "Quantencomputing", description: "Entwirf Quantenschaltungen und führe sie auf echter Quantenhardware aus — Logik auf Gatterebene, Grundlagen der Fehlerkorrektur und eine live durchgeführte hybride klassisch-Quanten-Demonstration.", modules: ["Montage", "Elektronik", "3D-Gehäusedesign", "Firmware", "Quantenschaltungen"] },
  tr: { title: "Kuantum Bilişim", tagline: "Kübitler, kapılar ve gerçek donanım.", category: "Kuantum Bilişim", description: "Kuantum devreleri tasarlayın ve bunları gerçek kuantum donanımında çalıştırın — kapı seviyesinde mantık, hata temelleri ve canlı hibrit klasik-kuantum gösterimi.", modules: ["Montaj", "Elektronik", "3D Kasa Tasarımı", "Firmware", "Kuantum Devreleri"] },
  ru: { title: "Квантовые вычисления", tagline: "Кубиты, вентили и настоящее железо.", category: "Квантовые вычисления", description: "Проектируйте квантовые схемы и запускайте их на настоящем квантовом оборудовании — логика на уровне вентилей, основы коррекции ошибок и живая гибридная классическо-квантовая демонстрация.", modules: ["Сборка", "Электроника", "3D-проектирование корпуса", "Прошивка", "Квантовые схемы"] },
},
"DHS-RB": {
  en: { title: "Advanced Robotics", tagline: "Actuation, control, cognition.", category: "Advanced Robotics", description: "Build and control an articulated robotic arm from scratch — motor actuation, closed-loop control, and a live autonomous pick-and-place demonstration.", modules: ["Assembly", "Electronics", "3D Casing Design", "Firmware", "Robotic Control"] },
  de: { title: "Fortgeschrittene Robotik", tagline: "Aktorik, Regelung, Kognition.", category: "Fortgeschrittene Robotik", description: "Baue und steuere von Grund auf einen gelenkigen Roboterarm — Motoraktorik, Regelkreise und eine live durchgeführte autonome Pick-and-Place-Demonstration.", modules: ["Montage", "Elektronik", "3D-Gehäusedesign", "Firmware", "Robotersteuerung"] },
  tr: { title: "İleri Robotik", tagline: "Tahrik, kontrol, biliş.", category: "İleri Robotik", description: "Sıfırdan eklemli bir robot kolu inşa edin ve kontrol edin — motor tahriki, kapalı döngü kontrol ve canlı bir otonom alma-yerleştirme gösterimi.", modules: ["Montaj", "Elektronik", "3D Kasa Tasarımı", "Firmware", "Robotik Kontrol"] },
  ru: { title: "Продвинутая робототехника", tagline: "Привод, управление, восприятие.", category: "Продвинутая робототехника", description: "Соберите и управляйте шарнирной роботизированной рукой с нуля — привод двигателей, управление с обратной связью и живая демонстрация автономного захвата и перемещения объектов.", modules: ["Сборка", "Электроника", "3D-проектирование корпуса", "Прошивка", "Управление роботом"] },
},
  "DHS-AU": {
    en: { title: "Autonomous Systems", tagline: "SLAM, inference, closed-loop — live.", category: "Autonomous Systems", description: "SLAM, inference, and closed-loop control — build and deploy an autonomous system live, from sensors to decision-making.", modules: ["Assembly", "Electronics", "3D Casing Design", "Firmware", "Autonomous Navigation"] },
    de: { title: "Autonome Systeme", tagline: "SLAM, Inferenz, Regelkreis — live.", category: "Autonome Systeme", description: "SLAM, Inferenz und geschlossene Regelkreise — baue und setze live ein autonomes System ein, von den Sensoren bis zur Entscheidungsfindung.", modules: ["Montage", "Elektronik", "3D-Gehäusedesign", "Firmware", "Autonome Navigation"] },
    tr: { title: "Otonom Sistemler", tagline: "SLAM, çıkarım, kapalı döngü — canlı.", category: "Otonom Sistemler", description: "SLAM, çıkarım ve kapalı döngü kontrol — sensörlerden karar vermeye kadar canlı olarak otonom bir sistem inşa edin ve devreye alın.", modules: ["Montaj", "Elektronik", "3D Kasa Tasarımı", "Firmware", "Otonom Navigasyon"] },
    ru: { title: "Автономные системы", tagline: "SLAM, инференс, замкнутый контур — вживую.", category: "Автономные системы", description: "SLAM, инференс и управление с обратной связью — соберите и запустите автономную систему вживую, от датчиков до принятия решений.", modules: ["Сборка", "Электроника", "3D-проектирование корпуса", "Прошивка", "Автономная навигация"] },
  },
};

function formatDates(dates, t) {
  const vals = Object.values(dates).filter((d) => d && d !== "TBD");
  if (vals.length === 0) return t.tbdShort;
  if (vals.length === 1) return vals[0];
  if (Object.values(dates).every((d) => d && d !== "TBD")) return `${vals[0]} – ${vals[vals.length - 1]}`;
  return `${vals.length} ${t.of} ${Object.keys(dates).length} ${t.daysConfirmed}`;
}
function allDatesTBD(dates) { return Object.values(dates).every((d) => !d || d === "TBD"); }
function generateAppId(workshopId) { return `${workshopId}_${Date.now()}_${Math.random().toString(36).slice(2, 7).toUpperCase()}`; }
function workshopHash(ws) { return ws.id.toLowerCase(); }
function workshopUrl(ws) {
  if (typeof window === "undefined") return "";
  return `${window.location.origin}${window.location.pathname}#${workshopHash(ws)}`;
}

// ── Translations ─────────────────────────────────────────────────
const T = {
  en: {
    eyebrow: "Deep Tech Programme",
    titleLine1: "Build the",
    titleLine2: "Future",
    subtitle: "Hands-on intensive workshops covering the full stack of deep technology — from firmware to physical assembly to live systems testing.",
    locationNote: `Aerospace confirmed — 25 July 2026 at ${VENUE}. Other domains: date & location to be announced.`,
    comingSoon: "Coming Soon",
    notOpenYet: "Not Open Yet",
    apply: "Apply",
    scheduleLabel: "Schedule",
    tbdNote: "Date & location to be announced",
    tbdShort: "Dates to be confirmed",
    of: "of",
    daysConfirmed: "days confirmed",
    close: "Close",
    back: "Back",
    ok: "OK",
    enterHint: "to continue",
    payHint: (price) => `A one-time workshop fee of €${price}. Paid securely via Stripe.`,
    payButton: (price) => `Pay €${price} & Apply`,
    processing: "Processing…",
    retry: "Retry",
    preparingPayment: "Preparing payment…",
    stripeSecured: "Secured by Stripe · 256-bit SSL encryption",
    priceLabel: "Workshop registration fee",
    questionOf: (i, total) => `Question ${i} of ${total}`,
    submittingTitle: "Submitting your application",
    submittingSteps: ["Uploading CV to secure storage…", "Saving your application…", "Almost done…"],
    successTitle: "Application Received",
    successSub: (title, location) => <>You've applied for <strong style={{ color: "#fff" }}>{title}</strong> at {location}. Payment confirmed — our team will review your profile carefully.</>,
    successNoteConfirmed: (location, dates) => `See you at ${location} on ${dates}.`,
    successNoteTBD: "Confirmed date & location will be sent to your email.",
    successContact: "Questions? Reach us at",
    backToWorkshops: "← Back to workshops",
    coupon: {
      label: "Have a coupon code?",
      placeholder: "Enter code",
      apply: "Apply",
      remove: "Remove",
      invalid: "That coupon code isn't valid.",
      applied: (pct) => `Coupon applied — ${pct}% off`,
    },
    steps: {
      name: { label: "What's your full name?", hint: "Let's start with the basics.", placeholder: "Jane Doe" },
      age: { label: "How old are you?", hint: "We're open to all career stages.", placeholder: "24" },
      industry: { label: "Which industry are you in?", hint: "Choose the one that best fits your background." },
      occupation: { label: "What's your current role?", hint: "Job title or description works great.", placeholder: "e.g. Aerospace Engineer" },
      phone: { label: "Your contact number?", hint: "Include your country code — we work globally.", placeholder: "+44 7700 900000" },
      email: { label: "Your email address?", hint: "Location & confirmation will be sent here if selected.", placeholder: "jane@example.com" },
      cv: { label: "Upload your CV", hint: "PDF, DOC, or DOCX — max 5 MB.", dragDrop: "Drag & drop or", browse: "browse files", fileTypes: "PDF, DOC, DOCX — max 5 MB", replace: "click to replace" },
      github: { label: "GitHub or portfolio link?", hint: "Optional — share relevant projects.", placeholder: "https://github.com/yourname" },
      terms: { label: "Almost there.", hint: "Accept our terms before proceeding to payment.", pre: "I agree to DHS's", and: "and", post: "I understand that confirmed date and location details will be emailed to me upon selection." },
      payment: { label: "Secure your spot." },
    },
    industries: ["Technology", "Defence & Aerospace", "Biotech / Healthcare", "Academic / Research", "Engineering", "Other"],
    validation: { required: "This field is required.", email: "Please enter a valid email.", age: "Please enter a valid age (16–99).", choice: "Please select an option.", file: "Please upload your CV.", terms: "You must accept the terms to continue." },
    legal: {
      tcLinkText: "Terms & Conditions",
      privacyLinkText: "Privacy Policy",
      tcTitle: "Terms & Conditions",
      privacyTitle: "Privacy Policy",
      page: "Page",
      next: "Next",
      prev: "Previous",
      tcPages: [
        { title: "1. Acceptance of Terms", body: "By registering for a Dead Hardware Society (DHS) workshop, you agree to be bound by these Terms & Conditions in full. If you do not agree, please do not complete the application or payment." },
        { title: "2. Eligibility & Registration", body: "Applicants must be at least 16 years old and provide accurate personal and professional information. DHS reviews every application and reserves the right to decline a place at its own discretion, in which case any fee paid will be refunded in full." },
        { title: "3. Fees & Payment", body: "Workshop fees are shown in euros and processed securely via Stripe. Once your application is accepted, the fee is non-refundable, except where DHS cancels or materially changes the workshop." },
        { title: "4. Conduct & Safety", body: "These workshops involve tools, electronics, and hardware that can be hazardous if mishandled. Participants must follow all instructor safety guidance at all times. DHS may remove any participant who disregards safety instructions, without refund." },
        { title: "5. Liability & Changes", body: "DHS reserves the right to change workshop dates, venue, or content where necessary, and will notify confirmed participants by email. To the extent permitted by law, DHS's liability is limited to the fee paid for the workshop." },
      ],
      privacyPages: [
        { title: "1. Data We Collect", body: "We collect the information you submit in the application form: name, age, industry, occupation, phone number, email address, CV file, and an optional portfolio link." },
        { title: "2. How We Use Your Data", body: "Your data is used to review your application, communicate confirmed dates and location, process payment, and contact you about the workshop you applied for." },
        { title: "3. Data Storage & Security", body: "Application data and CVs are stored securely using Firebase. Payment details are handled entirely by Stripe and are never stored on DHS servers." },
        { title: "4. Third Parties", body: "We share data only with the service providers needed to run the workshop: Stripe for payment processing and Firebase for secure storage. We do not sell your data to third parties." },
        { title: "5. Your Rights", body: `You may request access to, correction of, or deletion of your data at any time by contacting us at ${CONTACT_EMAIL}. You also have the right to lodge a complaint with your local data protection authority.` },
      ],
    },
  },
  de: {
    eyebrow: "Deep-Tech-Programm",
    titleLine1: "Gestalte die",
    titleLine2: "Zukunft",
    subtitle: "Praxisnahe Intensiv-Workshops über den gesamten Deep-Tech-Stack — von der Firmware über den physischen Zusammenbau bis zum Live-Systemtest.",
    locationNote: `Luft- und Raumfahrt bestätigt — 25. Juli 2026 im ${VENUE}. Weitere Bereiche: Datum & Ort werden noch bekannt gegeben.`,
    comingSoon: "Demnächst",
    notOpenYet: "Noch nicht geöffnet",
    apply: "Bewerben",
    scheduleLabel: "Termin",
    tbdNote: "Datum & Ort werden noch bekannt gegeben",
    tbdShort: "Termine folgen",
    of: "von",
    daysConfirmed: "Tage bestätigt",
    close: "Schließen",
    back: "Zurück",
    ok: "OK",
    enterHint: "zum Fortfahren",
    payHint: (price) => `Eine einmalige Workshop-Gebühr von €${price}. Sicher bezahlt über Stripe.`,
    payButton: (price) => `€${price} bezahlen & bewerben`,
    processing: "Wird verarbeitet…",
    retry: "Erneut versuchen",
    preparingPayment: "Zahlung wird vorbereitet…",
    stripeSecured: "Gesichert durch Stripe · 256-Bit-SSL-Verschlüsselung",
    priceLabel: "Workshop-Anmeldegebühr",
    questionOf: (i, total) => `Frage ${i} von ${total}`,
    submittingTitle: "Deine Bewerbung wird gesendet",
    submittingSteps: ["Lebenslauf wird sicher hochgeladen…", "Bewerbung wird gespeichert…", "Fast fertig…"],
    successTitle: "Bewerbung eingegangen",
    successSub: (title, location) => <>Du hast dich für <strong style={{ color: "#fff" }}>{title}</strong> in {location} beworben. Zahlung bestätigt — unser Team prüft dein Profil sorgfältig.</>,
    successNoteConfirmed: (location, dates) => `Wir sehen uns am ${dates} im ${location}.`,
    successNoteTBD: "Bestätigtes Datum & Ort werden dir per E-Mail zugesendet.",
    successContact: "Fragen? Erreiche uns unter",
    backToWorkshops: "← Zurück zu den Workshops",
    coupon: {
      label: "Hast du einen Gutscheincode?",
      placeholder: "Code eingeben",
      apply: "Anwenden",
      remove: "Entfernen",
      invalid: "Dieser Gutscheincode ist ungültig.",
      applied: (pct) => `Gutschein angewendet — ${pct}% Rabatt`,
    },
    steps: {
      name: { label: "Wie lautet dein vollständiger Name?", hint: "Fangen wir mit den Grundlagen an.", placeholder: "Jane Doe" },
      age: { label: "Wie alt bist du?", hint: "Wir stehen allen Karrierestufen offen.", placeholder: "24" },
      industry: { label: "In welcher Branche bist du tätig?", hint: "Wähle die Option, die am besten zu deinem Hintergrund passt." },
      occupation: { label: "Was ist deine aktuelle Rolle?", hint: "Berufsbezeichnung oder kurze Beschreibung reicht völlig.", placeholder: "z. B. Luft- und Raumfahrtingenieurin" },
      phone: { label: "Deine Telefonnummer?", hint: "Bitte mit Ländervorwahl — wir arbeiten global.", placeholder: "+49 170 1234567" },
      email: { label: "Deine E-Mail-Adresse?", hint: "Ort & Bestätigung werden hierher gesendet, falls ausgewählt.", placeholder: "jane@example.com" },
      cv: { label: "Lebenslauf hochladen", hint: "PDF, DOC oder DOCX — max. 5 MB.", dragDrop: "Datei hierher ziehen oder", browse: "Datei auswählen", fileTypes: "PDF, DOC, DOCX — max. 5 MB", replace: "klicken zum Ersetzen" },
      github: { label: "GitHub- oder Portfolio-Link?", hint: "Optional — teile relevante Projekte.", placeholder: "https://github.com/deinname" },
      terms: { label: "Fast geschafft.", hint: "Akzeptiere unsere Bedingungen, bevor es zur Zahlung geht.", pre: "Ich stimme den", and: "und der", post: "Ich verstehe, dass mir bestätigtes Datum und Ort per E-Mail zugesendet werden, sofern ich ausgewählt werde." },
      payment: { label: "Sichere dir deinen Platz." },
    },
    industries: ["Technologie", "Verteidigung & Luftfahrt", "Biotech / Gesundheitswesen", "Akademisch / Forschung", "Ingenieurwesen", "Sonstiges"],
    validation: { required: "Dieses Feld ist erforderlich.", email: "Bitte gib eine gültige E-Mail-Adresse ein.", age: "Bitte gib ein gültiges Alter an (16–99).", choice: "Bitte wähle eine Option.", file: "Bitte lade deinen Lebenslauf hoch.", terms: "Du musst den Bedingungen zustimmen, um fortzufahren." },
    legal: {
      tcLinkText: "Allgemeinen Geschäftsbedingungen",
      privacyLinkText: "Datenschutzerklärung",
      tcTitle: "Allgemeine Geschäftsbedingungen",
      privacyTitle: "Datenschutzerklärung",
      page: "Seite",
      next: "Weiter",
      prev: "Zurück",
      tcPages: [
        { title: "1. Annahme der Bedingungen", body: "Mit der Anmeldung zu einem Workshop der Dead Hardware Society (DHS) erklärst du dich vollumfänglich mit diesen Allgemeinen Geschäftsbedingungen einverstanden. Falls du nicht zustimmst, schließe die Bewerbung oder Zahlung bitte nicht ab." },
        { title: "2. Teilnahmeberechtigung & Anmeldung", body: "Teilnehmende müssen mindestens 16 Jahre alt sein und korrekte persönliche und berufliche Angaben machen. DHS prüft jede Bewerbung und behält sich vor, einen Platz nach eigenem Ermessen abzulehnen; in diesem Fall wird eine bereits gezahlte Gebühr vollständig erstattet." },
        { title: "3. Gebühren & Zahlung", body: "Workshop-Gebühren werden in Euro angezeigt und sicher über Stripe abgewickelt. Nach Annahme deiner Bewerbung ist die Gebühr nicht erstattungsfähig, außer DHS sagt den Workshop ab oder ändert ihn wesentlich." },
        { title: "4. Verhalten & Sicherheit", body: "Diese Workshops beinhalten Werkzeuge, Elektronik und Hardware, die bei unsachgemäßem Umgang gefährlich sein können. Teilnehmende müssen jederzeit den Sicherheitsanweisungen der Kursleitung folgen. DHS kann Teilnehmende, die Sicherheitsanweisungen missachten, ohne Erstattung ausschließen." },
        { title: "5. Haftung & Änderungen", body: "DHS behält sich vor, Datum, Ort oder Inhalt eines Workshops bei Bedarf zu ändern, und informiert bestätigte Teilnehmende per E-Mail. Soweit gesetzlich zulässig, ist die Haftung von DHS auf die gezahlte Workshop-Gebühr begrenzt." },
      ],
      privacyPages: [
        { title: "1. Erhobene Daten", body: "Wir erheben die im Bewerbungsformular angegebenen Daten: Name, Alter, Branche, Beruf, Telefonnummer, E-Mail-Adresse, Lebenslauf-Datei sowie optional einen Portfolio-Link." },
        { title: "2. Verwendung deiner Daten", body: "Deine Daten werden verwendet, um deine Bewerbung zu prüfen, bestätigtes Datum und Ort mitzuteilen, die Zahlung abzuwickeln und dich zum gebuchten Workshop zu kontaktieren." },
        { title: "3. Speicherung & Sicherheit", body: "Bewerbungsdaten und Lebensläufe werden sicher über Firebase gespeichert. Zahlungsdaten werden ausschließlich von Stripe verarbeitet und niemals auf DHS-Servern gespeichert." },
        { title: "4. Dritte", body: "Wir teilen Daten nur mit den Dienstleistern, die für die Durchführung des Workshops nötig sind: Stripe für die Zahlungsabwicklung und Firebase für die sichere Speicherung. Wir verkaufen deine Daten nicht an Dritte." },
        { title: "5. Deine Rechte", body: `Du kannst jederzeit Auskunft, Berichtigung oder Löschung deiner Daten verlangen, indem du uns unter ${CONTACT_EMAIL} kontaktierst. Du hast außerdem das Recht, dich bei deiner zuständigen Datenschutzbehörde zu beschweren.` },
      ],
    },
  },
  tr: {
    eyebrow: "İleri Teknoloji Programı",
    titleLine1: "Geleceği",
    titleLine2: "İnşa Et",
    subtitle: "Firmware'den fiziksel montaja, canlı sistem testlerine kadar ileri teknolojinin tamamını kapsayan uygulamalı yoğun atölyeler.",
    locationNote: `Havacılık ve Uzay onaylandı — 25 Temmuz 2026, ${VENUE}. Diğer alanlar: tarih ve konum daha sonra duyurulacak.`,
    comingSoon: "Yakında",
    notOpenYet: "Henüz Açık Değil",
    apply: "Başvur",
    scheduleLabel: "Program",
    tbdNote: "Tarih ve konum daha sonra duyurulacak",
    tbdShort: "Tarihler netleştirilecek",
    of: "/",
    daysConfirmed: "gün onaylandı",
    close: "Kapat",
    back: "Geri",
    ok: "Tamam",
    enterHint: "devam etmek için",
    payHint: (price) => `Tek seferlik €${price} atölye ücreti. Stripe ile güvenli ödeme.`,
    payButton: (price) => `€${price} Öde ve Başvur`,
    processing: "İşleniyor…",
    retry: "Tekrar dene",
    preparingPayment: "Ödeme hazırlanıyor…",
    stripeSecured: "Stripe ile güvence altına alınmıştır · 256-bit SSL şifreleme",
    priceLabel: "Atölye kayıt ücreti",
    questionOf: (i, total) => `Soru ${i} / ${total}`,
    submittingTitle: "Başvurun gönderiliyor",
    submittingSteps: ["Özgeçmiş güvenli depolamaya yükleniyor…", "Başvurun kaydediliyor…", "Neredeyse tamam…"],
    successTitle: "Başvuru Alındı",
    successSub: (title, location) => <><strong style={{ color: "#fff" }}>{title}</strong> atölyesine {location} için başvurdun. Ödeme onaylandı — ekibimiz profilini dikkatle inceleyecek.</>,
    successNoteConfirmed: (location, dates) => `${dates} tarihinde ${location} adresinde görüşmek üzere.`,
    successNoteTBD: "Onaylanan tarih ve konum e-postana gönderilecek.",
    successContact: "Sorularınız mı var? Bize ulaşın:",
    backToWorkshops: "← Atölyelere dön",
    coupon: {
      label: "Kupon kodun var mı?",
      placeholder: "Kodu girin",
      apply: "Uygula",
      remove: "Kaldır",
      invalid: "Bu kupon kodu geçerli değil.",
      applied: (pct) => `Kupon uygulandı — %${pct} indirim`,
    },
    steps: {
      name: { label: "Tam adın nedir?", hint: "Basit bilgilerle başlayalım.", placeholder: "Jane Doe" },
      age: { label: "Kaç yaşındasın?", hint: "Her kariyer seviyesine açığız.", placeholder: "24" },
      industry: { label: "Hangi sektörde çalışıyorsun?", hint: "Geçmişine en uygun olanı seç." },
      occupation: { label: "Mevcut rolün nedir?", hint: "İş unvanı veya kısa açıklama yeterli.", placeholder: "örn. Havacılık Mühendisi" },
      phone: { label: "İletişim numaran?", hint: "Ülke kodunu da ekle — global çalışıyoruz.", placeholder: "+90 555 000 00 00" },
      email: { label: "E-posta adresin?", hint: "Seçilirsen konum ve onay buraya gönderilecek.", placeholder: "jane@example.com" },
      cv: { label: "Özgeçmişini yükle", hint: "PDF, DOC veya DOCX — maks. 5 MB.", dragDrop: "Sürükle bırak ya da", browse: "dosya seç", fileTypes: "PDF, DOC, DOCX — maks. 5 MB", replace: "değiştirmek için tıkla" },
      github: { label: "GitHub veya portfolyo bağlantısı?", hint: "İsteğe bağlı — ilgili projelerini paylaş.", placeholder: "https://github.com/kullaniciadi" },
      terms: { label: "Neredeyse tamam.", hint: "Ödemeye geçmeden önce şartlarımızı kabul et.", pre: "DHS'nin", and: "ve", post: "Seçilmem durumunda onaylanan tarih ve konum bilgilerinin e-posta ile gönderileceğini anlıyorum." },
      payment: { label: "Yerini garantiye al." },
    },
    industries: ["Teknoloji", "Savunma ve Havacılık", "Biyoteknoloji / Sağlık", "Akademik / Araştırma", "Mühendislik", "Diğer"],
    validation: { required: "Bu alan zorunludur.", email: "Lütfen geçerli bir e-posta adresi girin.", age: "Lütfen geçerli bir yaş girin (16–99).", choice: "Lütfen bir seçenek belirleyin.", file: "Lütfen özgeçmişini yükle.", terms: "Devam etmek için şartları kabul etmelisin." },
    legal: {
      tcLinkText: "Şartlar ve Koşullar",
      privacyLinkText: "Gizlilik Politikası",
      tcTitle: "Şartlar ve Koşullar",
      privacyTitle: "Gizlilik Politikası",
      page: "Sayfa",
      next: "İleri",
      prev: "Geri",
      tcPages: [
        { title: "1. Şartların Kabulü", body: "Dead Hardware Society (DHS) atölyesine kaydolarak bu Şartlar ve Koşulları eksiksiz kabul etmiş olursun. Kabul etmiyorsan lütfen başvuruyu veya ödemeyi tamamlama." },
        { title: "2. Uygunluk ve Kayıt", body: "Başvuranlar en az 16 yaşında olmalı ve doğru kişisel/mesleki bilgi vermelidir. DHS her başvuruyu inceler ve kendi takdirine bağlı olarak bir yeri reddetme hakkını saklı tutar; bu durumda ödenen ücret tam olarak iade edilir." },
        { title: "3. Ücretler ve Ödeme", body: "Atölye ücretleri euro cinsinden gösterilir ve Stripe üzerinden güvenli şekilde işlenir. Başvurun kabul edildikten sonra ücret, DHS atölyeyi iptal etmediği veya önemli ölçüde değiştirmediği sürece iade edilmez." },
        { title: "4. Davranış ve Güvenlik", body: "Bu atölyeler, yanlış kullanıldığında tehlikeli olabilecek aletler, elektronik ve donanım içerir. Katılımcılar her zaman eğitmenin güvenlik talimatlarına uymalıdır. DHS, güvenlik talimatlarını göz ardı eden katılımcıları iade yapmadan çıkarma hakkına sahiptir." },
        { title: "5. Sorumluluk ve Değişiklikler", body: "DHS, gerektiğinde atölye tarihini, mekanını veya içeriğini değiştirme hakkını saklı tutar ve onaylanan katılımcıları e-posta ile bilgilendirir. Yasaların izin verdiği ölçüde DHS'nin sorumluluğu, atölye için ödenen ücretle sınırlıdır." },
      ],
      privacyPages: [
        { title: "1. Topladığımız Veriler", body: "Başvuru formunda gönderdiğin bilgileri topluyoruz: ad, yaş, sektör, meslek, telefon numarası, e-posta adresi, özgeçmiş dosyası ve isteğe bağlı bir portfolyo bağlantısı." },
        { title: "2. Verilerini Nasıl Kullanıyoruz", body: "Verilerin, başvurunu değerlendirmek, onaylanan tarih ve konumu bildirmek, ödemeyi işlemek ve başvurduğun atölye hakkında seninle iletişime geçmek için kullanılır." },
        { title: "3. Veri Saklama ve Güvenlik", body: "Başvuru verileri ve özgeçmişler Firebase kullanılarak güvenli şekilde saklanır. Ödeme bilgileri tamamen Stripe tarafından işlenir ve hiçbir zaman DHS sunucularında saklanmaz." },
        { title: "4. Üçüncü Taraflar", body: "Verileri yalnızca atölyeyi yürütmek için gereken hizmet sağlayıcılarla paylaşırız: ödeme işlemleri için Stripe ve güvenli depolama için Firebase. Verilerini üçüncü taraflara satmayız." },
        { title: "5. Haklarınız", body: `${CONTACT_EMAIL} adresinden bize ulaşarak verilerine erişim, düzeltme veya silme talebinde her zaman bulunabilirsin. Ayrıca yerel veri koruma otoritene şikâyette bulunma hakkına da sahipsin.` },
      ],
    },
  },
  ru: {
    eyebrow: "Программа Deep Tech",
    titleLine1: "Создавай",
    titleLine2: "Будущее",
    subtitle: "Интенсивные практические воркшопы, охватывающие весь стек глубоких технологий — от прошивки до физической сборки и живого тестирования систем.",
    locationNote: `Аэрокосмическое направление подтверждено — 25 июля 2026, ${VENUE}. Остальные направления: дата и место будут объявлены позже.`,
    comingSoon: "Скоро",
    notOpenYet: "Пока не открыто",
    apply: "Подать заявку",
    scheduleLabel: "Расписание",
    tbdNote: "Дата и место будут объявлены позже",
    tbdShort: "Даты уточняются",
    of: "из",
    daysConfirmed: "дней подтверждено",
    close: "Закрыть",
    back: "Назад",
    ok: "Далее",
    enterHint: "чтобы продолжить",
    payHint: (price) => `Единоразовый взнос за воркшоп €${price}. Безопасная оплата через Stripe.`,
    payButton: (price) => `Оплатить €${price} и подать заявку`,
    processing: "Обработка…",
    retry: "Повторить",
    preparingPayment: "Подготовка платежа…",
    stripeSecured: "Защищено Stripe · 256-битное SSL-шифрование",
    priceLabel: "Регистрационный взнос за воркшоп",
    questionOf: (i, total) => `Вопрос ${i} из ${total}`,
    submittingTitle: "Отправка заявки",
    submittingSteps: ["Загрузка резюме в защищённое хранилище…", "Сохранение заявки…", "Почти готово…"],
    successTitle: "Заявка получена",
    successSub: (title, location) => <>Вы подали заявку на <strong style={{ color: "#fff" }}>{title}</strong> в {location}. Оплата подтверждена — наша команда внимательно рассмотрит ваш профиль.</>,
    successNoteConfirmed: (location, dates) => `Ждём вас ${dates} в ${location}.`,
    successNoteTBD: "Подтверждённые дата и место будут отправлены на вашу почту.",
    successContact: "Вопросы? Свяжитесь с нами:",
    backToWorkshops: "← Назад к воркшопам",
    coupon: {
      label: "Есть промокод?",
      placeholder: "Введите код",
      apply: "Применить",
      remove: "Убрать",
      invalid: "Этот промокод недействителен.",
      applied: (pct) => `Промокод применён — скидка ${pct}%`,
    },
    steps: {
      name: { label: "Как вас зовут?", hint: "Начнём с самого простого.", placeholder: "Иван Иванов" },
      age: { label: "Сколько вам лет?", hint: "Мы открыты для всех этапов карьеры.", placeholder: "24" },
      industry: { label: "В какой отрасли вы работаете?", hint: "Выберите вариант, наиболее близкий к вашему опыту." },
      occupation: { label: "Кем вы сейчас работаете?", hint: "Достаточно должности или краткого описания.", placeholder: "напр. инженер аэрокосмической отрасли" },
      phone: { label: "Ваш контактный номер?", hint: "Укажите код страны — мы работаем по всему миру.", placeholder: "+7 900 000 00 00" },
      email: { label: "Ваш email?", hint: "Сюда придёт информация о месте и подтверждение, если вас выберут.", placeholder: "jane@example.com" },
      cv: { label: "Загрузите резюме", hint: "PDF, DOC или DOCX — макс. 5 МБ.", dragDrop: "Перетащите файл или", browse: "выберите файл", fileTypes: "PDF, DOC, DOCX — макс. 5 МБ", replace: "нажмите, чтобы заменить" },
      github: { label: "Ссылка на GitHub или портфолио?", hint: "Необязательно — поделитесь релевантными проектами.", placeholder: "https://github.com/username" },
      terms: { label: "Почти готово.", hint: "Примите условия перед переходом к оплате.", pre: "Я согласен(на) с", and: "и", post: "Я понимаю, что подтверждённые дата и место будут отправлены мне на почту в случае отбора." },
      payment: { label: "Забронируйте своё место." },
    },
    industries: ["Технологии", "Оборона и авиакосмическая отрасль", "Биотех / Здравоохранение", "Наука / Исследования", "Инженерия", "Другое"],
    validation: { required: "Это поле обязательно.", email: "Пожалуйста, введите корректный email.", age: "Пожалуйста, укажите корректный возраст (16–99).", choice: "Пожалуйста, выберите вариант.", file: "Пожалуйста, загрузите резюме.", terms: "Для продолжения необходимо принять условия." },
    legal: {
      tcLinkText: "Условиями использования",
      privacyLinkText: "Политикой конфиденциальности",
      tcTitle: "Условия использования",
      privacyTitle: "Политика конфиденциальности",
      page: "Страница",
      next: "Далее",
      prev: "Назад",
      tcPages: [
        { title: "1. Принятие условий", body: "Регистрируясь на воркшоп Dead Hardware Society (DHS), вы полностью соглашаетесь с настоящими Условиями использования. Если вы не согласны, пожалуйста, не завершайте заявку или оплату." },
        { title: "2. Право участия и регистрация", body: "Участникам должно быть не менее 16 лет, и они обязаны предоставить достоверные личные и профессиональные данные. DHS рассматривает каждую заявку и оставляет за собой право отклонить участие по своему усмотрению; в этом случае уплаченный взнос возвращается в полном объёме." },
        { title: "3. Взносы и оплата", body: "Стоимость воркшопа указана в евро и обрабатывается безопасно через Stripe. После принятия заявки взнос не подлежит возврату, за исключением случаев отмены или существенного изменения воркшопа со стороны DHS." },
        { title: "4. Поведение и безопасность", body: "Эти воркшопы включают инструменты, электронику и оборудование, которые могут быть опасны при неправильном обращении. Участники обязаны всегда следовать инструкциям инструктора по технике безопасности. DHS может удалить участника, игнорирующего правила безопасности, без возврата средств." },
        { title: "5. Ответственность и изменения", body: "DHS оставляет за собой право при необходимости изменить дату, место или содержание воркшопа и уведомит подтверждённых участников по email. В пределах, допустимых законом, ответственность DHS ограничена суммой уплаченного взноса." },
      ],
      privacyPages: [
        { title: "1. Какие данные мы собираем", body: "Мы собираем данные, указанные в форме заявки: имя, возраст, отрасль, должность, номер телефона, email, файл резюме и, при желании, ссылку на портфолио." },
        { title: "2. Как мы используем ваши данные", body: "Ваши данные используются для рассмотрения заявки, сообщения подтверждённых даты и места, обработки оплаты и связи с вами по поводу выбранного воркшопа." },
        { title: "3. Хранение и безопасность данных", body: "Данные заявок и резюме надёжно хранятся с использованием Firebase. Платёжные данные обрабатываются исключительно Stripe и никогда не хранятся на серверах DHS." },
        { title: "4. Третьи лица", body: "Мы передаём данные только поставщикам услуг, необходимым для проведения воркшопа: Stripe для обработки платежей и Firebase для безопасного хранения. Мы не продаём ваши данные третьим лицам." },
        { title: "5. Ваши права", body: `Вы можете в любой момент запросить доступ к своим данным, их исправление или удаление, написав нам на ${CONTACT_EMAIL}. Вы также имеете право подать жалобу в местный орган по защите данных.` },
      ],
    },
  },
};

function buildSteps(t) {
  return [
    { id: "name", label: t.steps.name.label, hint: t.steps.name.hint, field: "name", type: "text", placeholder: t.steps.name.placeholder },
    { id: "age", label: t.steps.age.label, hint: t.steps.age.hint, field: "age", type: "number", placeholder: t.steps.age.placeholder },
    { id: "industry", label: t.steps.industry.label, hint: t.steps.industry.hint, field: "industry", type: "choice", choices: t.industries },
    { id: "occupation", label: t.steps.occupation.label, hint: t.steps.occupation.hint, field: "occupation", type: "text", placeholder: t.steps.occupation.placeholder },
    { id: "phone", label: t.steps.phone.label, hint: t.steps.phone.hint, field: "phone", type: "tel", placeholder: t.steps.phone.placeholder },
    { id: "email", label: t.steps.email.label, hint: t.steps.email.hint, field: "email", type: "email", placeholder: t.steps.email.placeholder },
    { id: "cv", label: t.steps.cv.label, hint: t.steps.cv.hint, field: "cv", type: "file" },
    { id: "github", label: t.steps.github.label, hint: t.steps.github.hint, field: "github", type: "url", placeholder: t.steps.github.placeholder, optional: true },
    { id: "terms", label: t.steps.terms.label, hint: t.steps.terms.hint, field: "terms", type: "terms" },
    { id: "payment", label: t.steps.payment.label, hint: "", field: "payment", type: "payment" },
  ];
}

function validate(step, values, cvFile, t) {
  const { field, type, optional } = step;
  if (optional) return null;
  const v = values[field];
  if (type === "text" || type === "tel") { if (!v || v.trim().length < 2) return t.validation.required; }
  if (type === "email") { if (!v || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim())) return t.validation.email; }
  if (type === "number") { const n = parseInt(v); if (isNaN(n) || n < 16 || n > 99) return t.validation.age; }
  if (type === "choice" && !v) return t.validation.choice;
  if (type === "file" && !cvFile) return t.validation.file;
  if (type === "terms" && !v) return t.validation.terms;
  return null;
}

// ── Styles (darker, cyberpunk-leaning — video background kept) ───
const S = {
  root: { position: "relative", minHeight: "100vh", fontFamily: "'DM Sans','Helvetica Neue',sans-serif", background: "#020203", color: "#fff", overflow: "hidden" },
  video: { position: "fixed", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: 0.24, zIndex: 0, filter: "saturate(1.15) contrast(1.05)" },
  overlay: { position: "fixed", inset: 0, background: "linear-gradient(140deg,rgba(6,2,14,0.93) 0%,rgba(2,2,6,0.9) 55%,rgba(10,2,10,0.93) 100%)", zIndex: 1 },
  scanlines: { position: "fixed", inset: 0, zIndex: 2, pointerEvents: "none", backgroundImage: "repeating-linear-gradient(0deg, rgba(0,240,255,0.035) 0px, rgba(0,240,255,0.035) 1px, transparent 1px, transparent 3px)", mixBlendMode: "screen", opacity: 0.5 },
  brand: { position: "fixed", top: 28, left: 36, zIndex: 100, fontFamily: "'Syne','Helvetica Neue',sans-serif", fontWeight: 800, fontSize: 20, letterSpacing: -0.5, textShadow: "0 0 14px rgba(200,240,60,0.35)" },
  accent: { color: "#c8f03c" },
  accent2: { color: "#00f0ff" },
  langSwitcher: { position: "fixed", top: 24, right: 36, zIndex: 100, display: "flex", gap: 4, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(0,240,255,0.18)", borderRadius: 99, padding: 4, backdropFilter: "blur(14px)" },
  langBtn: (active) => ({ fontFamily: "'Syne',sans-serif", fontSize: 11, fontWeight: 800, letterSpacing: 0.5, padding: "6px 11px", borderRadius: 99, border: "none", cursor: "pointer", background: active ? "#c8f03c" : "transparent", color: active ? "#000" : "rgba(255,255,255,0.5)", transition: "all .18s" }),
  listPage: { position: "relative", zIndex: 10, minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", padding: "110px 24px 80px" },
  listHeader: { textAlign: "center", marginBottom: 56, maxWidth: 680 },
  listEyebrow: { fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: "uppercase", color: "#00f0ff", marginBottom: 16 },
  listTitle: { fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: "clamp(36px,5.5vw,68px)", letterSpacing: -2.5, lineHeight: 1.02, marginBottom: 18, textShadow: "0 0 30px rgba(139,92,246,0.25)" },
  listSub: { color: "rgba(255,255,255,0.4)", fontSize: 16, lineHeight: 1.7 },
  locationNote: { display: "inline-flex", alignItems: "center", gap: 8, marginTop: 20, padding: "8px 18px", background: "rgba(0,240,255,0.06)", border: "1px solid #00f0ff55", borderRadius: 99, fontSize: 13, color: "#00f0ff" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(320px,1fr))", gap: 16, width: "100%", maxWidth: 1080 },
  card: (hovered, color, disabled) => ({ background: hovered && !disabled ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.025)", border: hovered && !disabled ? `1px solid ${color}66` : "1px solid rgba(255,255,255,0.06)", borderRadius: 18, padding: "24px 26px 20px", cursor: disabled ? "not-allowed" : "pointer", transition: "all .28s cubic-bezier(.4,0,.2,1)", transform: hovered && !disabled ? "translateY(-4px)" : "none", backdropFilter: "blur(24px)", position: "relative", overflow: "hidden", opacity: disabled ? 0.5 : 1, boxShadow: hovered && !disabled ? `0 0 30px ${color}22` : "none" }),
  cardGlow: (color) => ({ position: "absolute", top: -50, right: -50, width: 140, height: 140, borderRadius: "50%", background: color, opacity: 0.06, pointerEvents: "none" }),
  cardTop: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 16, gap: 12 },
  cardImg: { width: 52, height: 52, borderRadius: 12, objectFit: "cover", flexShrink: 0, border: "1px solid rgba(255,255,255,0.1)" },
  cardBadge: (color) => ({ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color, background: `${color}18`, border: `1px solid ${color}33`, borderRadius: 6, padding: "4px 10px", whiteSpace: "nowrap" }),
  comingSoonBadge: { fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: "rgba(255,255,255,0.45)", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.14)", borderRadius: 6, padding: "4px 10px", whiteSpace: "nowrap" },
  cardTitle: { fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 16, lineHeight: 1.3, marginBottom: 9, color: "#fff" },
  cardDesc: { fontSize: 13, color: "rgba(255,255,255,0.36)", lineHeight: 1.65, marginBottom: 14 },
  cardModules: { display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 16 },
  modulePill: (color) => ({ fontSize: 11, padding: "3px 9px", borderRadius: 20, background: `${color}12`, color: `${color}cc`, border: `1px solid ${color}25` }),
  cardFooter: { display: "flex", alignItems: "center", justifyContent: "space-between", borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 14 },
  cardDateWrap: { display: "flex", flexDirection: "column", gap: 2 },
  cardDateLabel: { fontSize: 10, fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", color: "rgba(255,255,255,0.25)" },
  cardDate: (tbd) => ({ fontSize: 12, color: tbd ? "rgba(255,255,255,0.28)" : "rgba(255,255,255,0.65)", fontStyle: tbd ? "italic" : "normal" }),
  cardLocation: { fontSize: 11, color: "rgba(255,255,255,0.3)", marginTop: 2 },
  applyBtn: (color, disabled) => ({ fontSize: 13, fontWeight: 700, color: disabled ? "rgba(255,255,255,0.3)" : color, background: "none", border: "none", cursor: disabled ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 5, padding: 0, fontFamily: "'Syne',sans-serif", letterSpacing: 0.3, textDecoration: "none" }),
  modalBg: { position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.82)", backdropFilter: "blur(10px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 },
  panel: (v) => ({ position: "relative", width: "100%", maxWidth: 600, background: "rgba(6,5,12,0.97)", border: "1px solid rgba(0,240,255,0.12)", borderRadius: 24, padding: "44px 44px 36px", backdropFilter: "blur(40px)", maxHeight: "92vh", overflowY: "auto", opacity: v ? 1 : 0, transform: v ? "scale(1) translateY(0)" : "scale(0.94) translateY(16px)", transition: "opacity .35s, transform .35s" }),
  closeBtn: { position: "absolute", top: 18, right: 18, width: 34, height: 34, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.09)", borderRadius: 9, cursor: "pointer", color: "#fff", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center" },
  wsTag: (color) => ({ display: "inline-flex", alignItems: "center", gap: 10, background: `${color}10`, border: `1px solid ${color}28`, borderRadius: 10, padding: "8px 14px", marginBottom: 14 }),
  wsTagImg: { width: 28, height: 28, borderRadius: 7, objectFit: "cover" },
  wsTagText: (color) => ({ fontSize: 12, color, fontWeight: 700, letterSpacing: 0.2 }),
  wsMeta: { fontSize: 12, color: "rgba(255,255,255,0.4)", marginBottom: 24, display: "flex", flexWrap: "wrap", gap: 14 },
  wsMetaItem: { display: "flex", alignItems: "center", gap: 6 },
  formProgress: { display: "flex", gap: 3, marginBottom: 32 },
  dot: (active, done) => ({ flex: 1, height: 2, borderRadius: 99, background: done ? "#c8f03c" : active ? "rgba(200,240,60,0.4)" : "rgba(255,255,255,0.08)", transition: "background .3s" }),
  qNum: { fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", color: "#c8f03c", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 },
  qLine: { width: 16, height: 1, background: "#c8f03c", display: "inline-block" },
  question: { fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: "clamp(20px,3vw,30px)", lineHeight: 1.12, marginBottom: 6, letterSpacing: -0.5 },
  hint: { fontSize: 14, color: "rgba(255,255,255,0.38)", marginBottom: 24, lineHeight: 1.6 },
  input: (err) => ({ width: "100%", background: "rgba(255,255,255,0.05)", border: `1px solid ${err ? "#ff5e5e" : "rgba(255,255,255,0.1)"}`, borderRadius: 11, padding: "15px 18px", fontSize: 15, color: "#fff", outline: "none", fontFamily: "'DM Sans',sans-serif", transition: "border-color .25s, box-shadow .25s", boxSizing: "border-box" }),
  choices: { display: "flex", flexDirection: "column", gap: 7 },
  choiceBtn: (sel) => ({ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "13px 16px", borderRadius: 11, cursor: "pointer", fontSize: 14, background: sel ? "rgba(200,240,60,0.08)" : "rgba(255,255,255,0.04)", border: sel ? "1px solid rgba(200,240,60,0.45)" : "1px solid rgba(255,255,255,0.08)", color: "#fff", transition: "all .18s", textAlign: "left", fontFamily: "'DM Sans',sans-serif" }),
  choiceKey: (sel) => ({ width: 24, height: 24, borderRadius: 5, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, flexShrink: 0, background: sel ? "#c8f03c" : "transparent", color: sel ? "#000" : "rgba(255,255,255,0.3)", border: sel ? "1px solid #c8f03c" : "1px solid rgba(255,255,255,0.12)", transition: "all .18s" }),
  uploadArea: (drag, hasFile) => ({ border: `1.5px dashed ${drag ? "#c8f03c" : hasFile ? "rgba(200,240,60,0.4)" : "rgba(255,255,255,0.12)"}`, borderRadius: 11, padding: "30px 20px", textAlign: "center", cursor: "pointer", background: drag ? "rgba(200,240,60,0.05)" : hasFile ? "rgba(200,240,60,0.04)" : "rgba(255,255,255,0.03)", transition: "all .22s" }),
  errMsg: { fontSize: 12, color: "#ff5e5e", marginTop: 7, display: "flex", alignItems: "center", gap: 5 },
  tcWrap: (c) => ({ display: "flex", gap: 12, alignItems: "flex-start", padding: "15px 16px", borderRadius: 11, cursor: "pointer", background: "rgba(255,255,255,0.03)", border: `1px solid ${c ? "rgba(200,240,60,0.35)" : "rgba(255,255,255,0.08)"}`, transition: "border-color .2s" }),
  checkBox: (c) => ({ width: 20, height: 20, flexShrink: 0, borderRadius: 5, background: c ? "#c8f03c" : "transparent", border: `1.5px solid ${c ? "#c8f03c" : "rgba(255,255,255,0.18)"}`, display: "flex", alignItems: "center", justifyContent: "center", transition: "all .2s", marginTop: 1 }),
  tcText: { fontSize: 13, color: "rgba(255,255,255,0.45)", lineHeight: 1.6 },
  tcLink: { color: "#00f0ff", textDecoration: "underline", textUnderlineOffset: 2, cursor: "pointer" },
  btnRow: { display: "flex", alignItems: "center", gap: 10, marginTop: 24 },
  nextBtn: { display: "inline-flex", alignItems: "center", gap: 9, padding: "14px 28px", background: "#c8f03c", color: "#000", border: "none", borderRadius: 11, fontFamily: "'Syne',sans-serif", fontSize: 14, fontWeight: 800, cursor: "pointer", boxShadow: "0 4px 20px rgba(200,240,60,0.2)", transition: "transform .18s, box-shadow .2s", letterSpacing: 0.3 },
  backBtn: { padding: "13px 20px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.09)", borderRadius: 11, color: "rgba(255,255,255,0.6)", fontFamily: "'Syne',sans-serif", fontSize: 13, fontWeight: 700, cursor: "pointer" },
  kbdHint: { marginTop: 10, fontSize: 11, color: "rgba(255,255,255,0.18)", display: "flex", alignItems: "center", gap: 5 },
  kbd: { border: "1px solid rgba(255,255,255,0.12)", borderRadius: 3, padding: "1px 5px", fontSize: 10, color: "rgba(255,255,255,0.25)", fontFamily: "monospace" },
  success: { textAlign: "center", padding: "12px 0" },
  tick: { width: 68, height: 68, borderRadius: "50%", background: "rgba(200,240,60,0.1)", border: "2px solid #c8f03c", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 22px", animation: "pulseTick 1.8s ease infinite" },
  successTitle: { fontFamily: "'Syne',sans-serif", fontSize: 30, fontWeight: 800, letterSpacing: -1, marginBottom: 10 },
  successSub: { color: "rgba(255,255,255,0.4)", fontSize: 15, lineHeight: 1.7, maxWidth: 360, margin: "0 auto 10px" },
  successNote: { fontSize: 13, color: "rgba(200,240,60,0.7)", marginBottom: 8 },
  successContact: { fontSize: 12, color: "rgba(255,255,255,0.3)", marginBottom: 26 },
  successBackBtn: { padding: "12px 26px", background: "transparent", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 11, color: "#fff", fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer" },
  submittingWrap: { textAlign: "center", padding: "32px 0" },
  submittingSpinner: { width: 48, height: 48, border: "3px solid rgba(200,240,60,0.15)", borderTop: "3px solid #c8f03c", borderRadius: "50%", margin: "0 auto 20px", animation: "spin .8s linear infinite" },
  submittingText: { fontFamily: "'Syne',sans-serif", fontSize: 18, fontWeight: 700, marginBottom: 8 },
  submittingStep: { fontSize: 13, color: "rgba(255,255,255,0.35)" },
  priceBadge: { display: "flex", alignItems: "center", justifyContent: "space-between", background: "rgba(200,240,60,0.06)", border: "1px solid rgba(200,240,60,0.2)", borderRadius: 12, padding: "16px 20px", marginBottom: 20 },
  priceLabel: { fontSize: 13, color: "rgba(255,255,255,0.45)" },
  priceAmount: { fontFamily: "'Syne',sans-serif", fontSize: 24, fontWeight: 800, color: "#c8f03c", letterSpacing: -0.5 },
  priceAmountOld: { fontSize: 13, color: "rgba(255,255,255,0.3)", textDecoration: "line-through", marginRight: 8 },
  stripeLock: { display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "rgba(255,255,255,0.22)", marginTop: 14, marginBottom: 4 },
  footer: { position: "relative", zIndex: 10, textAlign: "center", padding: "0 24px 60px", color: "rgba(255,255,255,0.3)", fontSize: 13 },
  footerLink: { color: "#00f0ff", textDecoration: "none" },
  legalOverlay: { position: "fixed", inset: 0, zIndex: 300, background: "rgba(0,0,0,0.88)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 },
  legalPanel: { width: "100%", maxWidth: 560, background: "rgba(6,5,12,0.98)", border: "1px solid rgba(0,240,255,0.18)", borderRadius: 20, padding: "36px 38px 30px", maxHeight: "86vh", display: "flex", flexDirection: "column" },
  legalTitle: { fontFamily: "'Syne',sans-serif", fontSize: 22, fontWeight: 800, marginBottom: 4, color: "#fff" },
  legalPageDots: { display: "flex", gap: 5, margin: "16px 0 20px" },
  legalDot: (active) => ({ flex: 1, height: 3, borderRadius: 99, background: active ? "#00f0ff" : "rgba(255,255,255,0.1)", transition: "background .2s" }),
  legalBody: { fontSize: 14, color: "rgba(255,255,255,0.55)", lineHeight: 1.75, overflowY: "auto", paddingRight: 4, flex: 1, minHeight: 120 },
  legalPageTitle: { fontFamily: "'Syne',sans-serif", fontSize: 15, fontWeight: 700, color: "#c8f03c", marginBottom: 10 },
  legalFooter: { display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 22, paddingTop: 18, borderTop: "1px solid rgba(255,255,255,0.07)" },
  legalPageCount: { fontSize: 12, color: "rgba(255,255,255,0.3)" },
  legalNavBtn: (disabled) => ({ padding: "9px 16px", borderRadius: 9, border: "1px solid rgba(255,255,255,0.1)", background: disabled ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.06)", color: disabled ? "rgba(255,255,255,0.2)" : "#fff", fontFamily: "'Syne',sans-serif", fontSize: 12, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer" }),
  couponRow: { display: "flex", gap: 8, marginBottom: 16 },
  couponInput: (err) => ({ flex: 1, background: "rgba(255,255,255,0.05)", border: `1px solid ${err ? "#ff5e5e" : "rgba(255,255,255,0.1)"}`, borderRadius: 11, padding: "12px 16px", fontSize: 14, color: "#fff", outline: "none", fontFamily: "'DM Sans',sans-serif", boxSizing: "border-box", letterSpacing: 1 }),
  couponBtn: { padding: "12px 18px", background: "rgba(0,240,255,0.1)", border: "1px solid rgba(0,240,255,0.35)", borderRadius: 11, color: "#00f0ff", fontFamily: "'Syne',sans-serif", fontSize: 13, fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap" },
  couponAppliedRow: { display: "flex", alignItems: "center", justifyContent: "space-between", background: "rgba(200,240,60,0.07)", border: "1px solid rgba(200,240,60,0.3)", borderRadius: 11, padding: "12px 16px", marginBottom: 16 },
  couponAppliedText: { fontSize: 13, color: "#c8f03c", fontWeight: 700 },
  couponRemoveBtn: { background: "none", border: "none", color: "rgba(255,255,255,0.4)", fontSize: 12, cursor: "pointer", textDecoration: "underline" },
};

const STRIPE_APPEARANCE = {
  theme: "night",
  variables: {
    colorPrimary: "#c8f03c",
    colorBackground: "#0d0d14",
    colorText: "#ffffff",
    colorDanger: "#ff5e5e",
    fontFamily: "'DM Sans', sans-serif",
    borderRadius: "11px",
  },
  rules: {
    ".Input": { border: "1px solid rgba(255,255,255,0.1)", backgroundColor: "rgba(255,255,255,0.04)" },
    ".Input:focus": { border: "1px solid #c8f03c", boxShadow: "0 0 0 3px rgba(200,240,60,0.1)" },
    ".Tab": { border: "1px solid rgba(255,255,255,0.08)", backgroundColor: "rgba(255,255,255,0.03)" },
    ".Tab:hover": { border: "1px solid rgba(200,240,60,0.3)" },
    ".Tab--selected": { border: "1px solid #c8f03c", backgroundColor: "rgba(200,240,60,0.06)" },
    ".Label": { color: "rgba(255,255,255,0.5)" },
  },
};

// ── Legal document modal (5-page viewer for T&C / Privacy Policy) ─
function LegalModal({ type, onClose }) {
  const lang = useLang();
  const t = T[lang];
  const [page, setPage] = useState(0);
  const pages = type === "terms" ? t.legal.tcPages : t.legal.privacyPages;
  const title = type === "terms" ? t.legal.tcTitle : t.legal.privacyTitle;
  const total = pages.length;

  return (
    <div style={S.legalOverlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={S.legalPanel}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div style={S.legalTitle}>{title}</div>
          <button style={S.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={S.legalPageDots}>{pages.map((_, i) => <div key={i} style={S.legalDot(i === page)} />)}</div>
        <div style={S.legalBody}>
          <div style={S.legalPageTitle}>{pages[page].title}</div>
          <div>{pages[page].body}</div>
        </div>
        <div style={S.legalFooter}>
          <span style={S.legalPageCount}>{t.legal.page} {page + 1} / {total}</span>
          <div style={{ display: "flex", gap: 8 }}>
            <button style={S.legalNavBtn(page === 0)} disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>{t.legal.prev}</button>
            <button style={S.legalNavBtn(page === total - 1)} disabled={page === total - 1} onClick={() => setPage((p) => Math.min(total - 1, p + 1))}>{t.legal.next}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── CheckoutForm — mounted INSIDE Elements with a valid clientSecret ──
function CheckoutForm({ email, workshopTitle, price, originalPrice, appliedCoupon, onSuccess }) {
  const lang = useLang();
  const t = T[lang];
  const stripe = useStripe();
  const elements = useElements();
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState(null);

  const handlePay = async () => {
    if (!stripe || !elements) return;
    setPayError(null);
    setPaying(true);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { payment_method_data: { billing_details: { email } } },
      redirect: "if_required",
    });

    if (error) {
      setPayError(error.message);
      setPaying(false);
    } else if (paymentIntent && paymentIntent.status === "succeeded") {
      onSuccess(paymentIntent.id, price, appliedCoupon ? appliedCoupon.code : null);
    }
  };

  const hasDiscount = appliedCoupon && originalPrice !== price;

  return (
    <div>
      <div style={S.priceBadge}>
        <div>
          <div style={S.priceLabel}>{t.priceLabel}</div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.28)", marginTop: 2 }}>{workshopTitle}</div>
        </div>
        <div style={{ display: "flex", alignItems: "baseline" }}>
          {hasDiscount && <span style={S.priceAmountOld}>€{originalPrice}</span>}
          <span style={S.priceAmount}>€{price}</span>
        </div>
      </div>

      <PaymentElement options={{ layout: "tabs" }} />

      {payError && <div style={{ ...S.errMsg, marginTop: 12 }}>⚠ {payError}</div>}

      <div style={S.stripeLock}>
        <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0110 0v4" />
        </svg>
        {t.stripeSecured}
      </div>

      <div style={S.btnRow}>
        <button
          style={{ ...S.nextBtn, opacity: paying ? 0.7 : 1, cursor: paying ? "not-allowed" : "pointer" }}
          onClick={handlePay}
          disabled={paying || !stripe}
          onMouseEnter={(e) => { if (!paying) { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 28px rgba(200,240,60,0.35)"; } }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(200,240,60,0.2)"; }}
        >
          {paying
            ? <><div style={{ width: 14, height: 14, border: "2px solid rgba(0,0,0,0.3)", borderTop: "2px solid #000", borderRadius: "50%", animation: "spin .7s linear infinite" }} />{t.processing}</>
            : <>{t.payButton(price)} <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg></>
          }
        </button>
      </div>
    </div>
  );
}

// ── CouponBox — code entry + applied state, shown above the payment element ──
function CouponBox({ couponInput, setCouponInput, couponError, appliedCoupon, onApply, onRemove }) {
  const lang = useLang();
  const t = T[lang];

  if (appliedCoupon) {
    return (
      <div style={S.couponAppliedRow}>
        <span style={S.couponAppliedText}>✓ {t.coupon.applied(appliedCoupon.discount)}</span>
        <button style={S.couponRemoveBtn} onClick={onRemove}>{t.coupon.remove}</button>
      </div>
    );
  }

  return (
    <div>
      <div style={S.couponRow}>
        <input
          style={S.couponInput(couponError)}
          type="text"
          value={couponInput}
          placeholder={t.coupon.placeholder}
          onChange={(e) => setCouponInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onApply()}
        />
        <button style={S.couponBtn} onClick={onApply}>{t.coupon.apply}</button>
      </div>
      {couponError && <div style={{ ...S.errMsg, marginTop: -10, marginBottom: 14 }}>⚠ {couponError}</div>}
    </div>
  );
}

// ── PaymentStep — creates intent, then mounts Elements + CheckoutForm ──
function PaymentStep({ email, workshopTitle, price, onSuccess }) {
  const lang = useLang();
  const t = T[lang];
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  const [couponInput, setCouponInput] = useState("");
  const [couponError, setCouponError] = useState(null);
  const [appliedCoupon, setAppliedCoupon] = useState(null); // { code, discount }

  const finalPrice = appliedCoupon
    ? Math.max(0, Math.round(price * (1 - appliedCoupon.discount / 100) * 100) / 100)
    : price;

  const applyCoupon = () => {
    const code = couponInput.trim();
    if (!code) return;
    const discount = COUPONS[code];
    if (discount) {
      setAppliedCoupon({ code, discount });
      setCouponError(null);
    } else {
      setCouponError(t.coupon.invalid);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponInput("");
    setCouponError(null);
  };

  const createIntent = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const res = await fetch(BACKEND_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: finalPrice,
          currency: "eur",
          description: `DHS Workshop — ${workshopTitle}${appliedCoupon ? ` (coupon ${appliedCoupon.code}, -${appliedCoupon.discount}%)` : ""}`,
          email,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.clientSecret) throw new Error(data.error || "Failed to initialise payment.");
      setClientSecret(data.clientSecret);
    } catch (err) {
      setFetchError(err.message);
    }
    setLoading(false);
  }, [email, workshopTitle, finalPrice, appliedCoupon]);

  useEffect(() => { createIntent(); }, [createIntent]);

  return (
    <div>
      <CouponBox
        couponInput={couponInput}
        setCouponInput={setCouponInput}
        couponError={couponError}
        appliedCoupon={appliedCoupon}
        onApply={applyCoupon}
        onRemove={removeCoupon}
      />

      {loading ? (
        <div style={{ textAlign: "center", padding: "32px 0" }}>
          <div style={S.submittingSpinner} />
          <div style={{ fontSize: 14, color: "rgba(255,255,255,0.4)", marginTop: 8 }}>{t.preparingPayment}</div>
        </div>
      ) : fetchError ? (
        <div>
          <div style={{ ...S.errMsg, marginBottom: 16 }}>⚠ {fetchError}</div>
          <button style={S.nextBtn} onClick={createIntent}>{t.retry}</button>
        </div>
      ) : clientSecret ? (
        <Elements key={clientSecret} stripe={stripePromise} options={{ clientSecret, appearance: STRIPE_APPEARANCE }}>
          <CheckoutForm
            email={email}
            workshopTitle={workshopTitle}
            price={finalPrice}
            originalPrice={price}
            appliedCoupon={appliedCoupon}
            onSuccess={onSuccess}
          />
        </Elements>
      ) : null}
    </div>
  );
}

// ── Particles ─────────────────────────────────────────────────────
function Particles() {
  const particles = useRef(Array.from({ length: 18 }, () => ({ size: Math.random() * 3 + 1.5, left: Math.random() * 100, duration: Math.random() * 14 + 8, delay: Math.random() * 14 }))).current;
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 2, pointerEvents: "none", overflow: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@300;400;500&display=swap');
        @keyframes floatUp{0%{transform:translateY(100vh) scale(0);opacity:0}10%{opacity:.14}90%{opacity:.03}100%{transform:translateY(-10vh) scale(1.6);opacity:0}}
        @keyframes pulseTick{0%,100%{box-shadow:0 0 0 0 rgba(200,240,60,.3)}50%{box-shadow:0 0 0 14px rgba(200,240,60,0)}}
        @keyframes spin{to{transform:rotate(360deg)}}
        input[type=number]::-webkit-inner-spin-button{-webkit-appearance:none}
        ::-webkit-scrollbar{width:4px}::-webkit-scrollbar-thumb{background:rgba(0,240,255,.25);border-radius:99px}
      `}</style>
      {particles.map((p, i) => <div key={i} style={{ position: "absolute", borderRadius: "50%", background: "#00f0ff", width: p.size, height: p.size, left: p.left + "%", animation: `floatUp ${p.duration}s ${p.delay}s linear infinite`, opacity: 0 }} />)}
    </div>
  );
}

function TextInput({ step, value, onChange, error, onEnter }) {
  return (
    <div>
      <input style={S.input(error)} type={step.type} value={value || ""} placeholder={step.placeholder}
        onChange={(e) => onChange(e.target.value)} onKeyDown={(e) => e.key === "Enter" && onEnter()} autoFocus
        onFocus={(e) => { e.target.style.borderColor = error ? "#ff5e5e" : "#c8f03c"; e.target.style.boxShadow = "0 0 0 3px rgba(200,240,60,0.1)"; }}
        onBlur={(e) => { e.target.style.borderColor = error ? "#ff5e5e" : "rgba(255,255,255,0.1)"; e.target.style.boxShadow = "none"; }} />
      {error && <div style={S.errMsg}>⚠ {error}</div>}
    </div>
  );
}

function ChoiceInput({ step, value, onChange, error }) {
  return (
    <div>
      <div style={S.choices}>
        {step.choices.map((c, i) => {
          const sel = value === c;
          return <button key={c} style={S.choiceBtn(sel)} onClick={() => onChange(c)}><span>{c}</span><span style={S.choiceKey(sel)}>{String.fromCharCode(65 + i)}</span></button>;
        })}
      </div>
      {error && <div style={S.errMsg}>⚠ {error}</div>}
    </div>
  );
}

function FileInput({ value, onChange, error }) {
  const lang = useLang();
  const t = T[lang];
  const [drag, setDrag] = useState(false);
  const inputRef = useRef();
  return (
    <div>
      <input ref={inputRef} type="file" accept=".pdf,.doc,.docx" style={{ display: "none" }} onChange={(e) => { if (e.target.files[0]) onChange(e.target.files[0]); }} />
      <div style={S.uploadArea(drag, !!value)} onClick={() => inputRef.current.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); if (e.dataTransfer.files[0]) onChange(e.dataTransfer.files[0]); }}>
        <svg width="30" height="30" fill="none" stroke={value ? "#c8f03c" : "rgba(255,255,255,0.3)"} strokeWidth="1.5" viewBox="0 0 24 24" style={{ display: "block", margin: "0 auto 10px" }}>
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
        </svg>
        {value
          ? <div><div style={{ fontSize: 14, color: "#c8f03c", fontWeight: 600, marginBottom: 4 }}>✓ {value.name}</div><div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)" }}>{(value.size / 1024 / 1024).toFixed(2)} MB — {t.steps.cv.replace}</div></div>
          : <div style={{ fontSize: 13, color: "rgba(255,255,255,0.3)", lineHeight: 1.6 }}>{t.steps.cv.dragDrop} <span style={{ color: "#00f0ff" }}>{t.steps.cv.browse}</span><div style={{ fontSize: 11, color: "rgba(255,255,255,0.2)", marginTop: 4 }}>{t.steps.cv.fileTypes}</div></div>}
      </div>
      {error && <div style={S.errMsg}>⚠ {error}</div>}
    </div>
  );
}

function TermsInput({ value, onChange, error, onOpenLegal }) {
  const lang = useLang();
  const t = T[lang];
  return (
    <div>
      <div style={S.tcWrap(value)} onClick={() => onChange(!value)}>
        <div style={S.checkBox(value)}>{value && <svg width="11" height="11" fill="none" stroke="#000" strokeWidth="3" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg>}</div>
        <div style={S.tcText}>
          {t.steps.terms.pre}{" "}
          <span style={S.tcLink} onClick={(e) => { e.stopPropagation(); onOpenLegal("terms"); }}>{t.legal.tcLinkText}</span>{" "}
          {t.steps.terms.and}{" "}
          <span style={S.tcLink} onClick={(e) => { e.stopPropagation(); onOpenLegal("privacy"); }}>{t.legal.privacyLinkText}</span>. {t.steps.terms.post}
        </div>
      </div>
      {error && <div style={S.errMsg}>⚠ {error}</div>}
    </div>
  );
}

function SubmittingScreen({ step }) {
  const lang = useLang();
  const t = T[lang];
  return (
    <div style={S.submittingWrap}>
      <div style={S.submittingSpinner} />
      <div style={S.submittingText}>{t.submittingTitle}</div>
      <div style={S.submittingStep}>{t.submittingSteps[Math.min(step, t.submittingSteps.length - 1)]}</div>
    </div>
  );
}

// ── Form Modal ────────────────────────────────────────────────────
function FormModal({ workshop, onClose }) {
  const lang = useLang();
  const t = T[lang];
  const wsText = WS_TEXT[workshop.id][lang];
  const STEPS = buildSteps(t);

  const [step, setStep] = useState(0);
  const [values, setValues] = useState({});
  const [cvFile, setCvFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitStep, setSubmitStep] = useState(0);
  const [done, setDone] = useState(false);
  const [visible, setVisible] = useState(false);
  const [legalOpen, setLegalOpen] = useState(null);

  useEffect(() => { const tm = setTimeout(() => setVisible(true), 20); return () => clearTimeout(tm); }, []);

  const cur = STEPS[step];
  const total = STEPS.length;

  const setValue = (val) => {
    if (cur.type === "file") { setCvFile(val); setErrors((e) => ({ ...e, cv: null })); }
    else { setValues((v) => ({ ...v, [cur.field]: val })); setErrors((e) => ({ ...e, [cur.field]: null })); }
  };

  const tryNext = useCallback(() => {
    const err = validate(cur, values, cvFile, t);
    if (err) { setErrors((e) => ({ ...e, [cur.field]: err })); return; }
    if (step < total - 1) setStep((s) => s + 1);
  }, [cur, values, cvFile, step, total, t]);

  const handlePaymentSuccess = useCallback(async (paymentIntentId, amountPaid, couponCode) => {
    setSubmitting(true);
    setSubmitStep(0);
    const appId = generateAppId(workshop.id);
    let cvDownloadUrl = "", cvStoragePath = "";
    try {
      const result = await uploadCV(cvFile, appId);
      cvDownloadUrl = result.url; cvStoragePath = result.path;
      setSubmitStep(1);
    } catch (err) { console.error("CV upload failed:", err); cvDownloadUrl = "upload_failed"; }
    try {
      await pushToFirebase({
        applicationId: appId, workshopId: workshop.id, workshopTitle: wsText.title,
        workshopCategory: wsText.category, workshopDates: workshop.dates,
        workshopLocation: workshop.location || "TBD",
        workshopLink: workshopUrl(workshop),
        name: values.name || "", age: values.age || "", industry: values.industry || "",
        occupation: values.occupation || "", phone: values.phone || "", email: values.email || "",
        cvFilename: cvFile ? cvFile.name : "", cvDownloadUrl, cvStoragePath,
        github: values.github || "", termsAccepted: true, applicationLanguage: lang,
        paymentIntentId, amountPaid: typeof amountPaid === "number" ? amountPaid : workshop.price, currency: "eur",
        couponCode: couponCode || null,
        contactEmail: CONTACT_EMAIL,
        submittedAt: new Date().toISOString(),
      });
    } catch (err) { console.error("DB write failed:", err); }
    setSubmitting(false);
    setDone(true);
  }, [values, cvFile, workshop, wsText, lang]);

  useEffect(() => {
    const handler = (e) => {
      if (legalOpen) return;
      if (e.key === "Escape" && !submitting) onClose();
      if (done || submitting || cur.type === "payment") return;
      if (e.key === "Enter" && cur.type !== "choice" && cur.type !== "file" && cur.type !== "terms") tryNext();
      if (cur.type === "choice") {
        const idx = e.key.toLowerCase().charCodeAt(0) - 97;
        if (idx >= 0 && idx < (cur.choices || []).length) {
          setValues((v) => ({ ...v, [cur.field]: cur.choices[idx] }));
          setErrors((e2) => ({ ...e2, [cur.field]: null }));
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [step, values, cvFile, done, submitting, cur, tryNext, onClose, legalOpen]);

  const isPaymentStep = cur.type === "payment";
  const location = workshop.location || t.tbdNote;

  return (
    <div style={S.modalBg} onClick={(e) => !submitting && e.target === e.currentTarget && onClose()}>
      <div style={S.panel(visible)}>
        {!submitting && <button style={S.closeBtn} onClick={onClose}>✕</button>}

        {submitting ? <SubmittingScreen step={submitStep} /> : !done ? (
          <>
            <div style={S.wsTag(workshop.color)}>
              <img src={workshop.imageUrl} alt="" style={S.wsTagImg} />
              <span style={S.wsTagText(workshop.color)}>{workshop.id} — {wsText.title}</span>
            </div>
            <div style={S.wsMeta}>
              <span style={S.wsMetaItem}>📍 {location}</span>
              <span style={S.wsMetaItem}>🗓 {formatDates(workshop.dates, t)}</span>
            </div>

            <div style={S.formProgress}>{STEPS.map((_, i) => <div key={i} style={S.dot(i === step, i < step)} />)}</div>
            <div style={S.qNum}><span style={S.qLine} /> {t.questionOf(step + 1, total)}</div>
            <h2 style={S.question}>{cur.label}</h2>
            <p style={S.hint}>{cur.type === "payment" ? t.payHint(workshop.price) : cur.hint}</p>

            {(cur.type === "text" || cur.type === "email" || cur.type === "tel" || cur.type === "url" || cur.type === "number") &&
              <TextInput step={cur} value={values[cur.field]} onChange={setValue} error={errors[cur.field]} onEnter={tryNext} />}
            {cur.type === "choice" && <ChoiceInput step={cur} value={values[cur.field]} onChange={setValue} error={errors[cur.field]} />}
            {cur.type === "file" && <FileInput value={cvFile} onChange={setValue} error={errors[cur.field]} />}
            {cur.type === "terms" && <TermsInput value={values[cur.field]} onChange={setValue} error={errors[cur.field]} onOpenLegal={setLegalOpen} />}

            {isPaymentStep && (
              <PaymentStep
                email={values.email}
                workshopTitle={wsText.title}
                price={workshop.price}
                onSuccess={handlePaymentSuccess}
              />
            )}

            {!isPaymentStep && (
              <div style={S.btnRow}>
                {step > 0 && <button style={S.backBtn} onClick={() => setStep((s) => s - 1)}>← {t.back}</button>}
                <button style={S.nextBtn} onClick={tryNext}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 28px rgba(200,240,60,0.35)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(200,240,60,0.2)"; }}>
                  {t.ok} <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
                </button>
              </div>
            )}

            {isPaymentStep && (
              <div style={{ marginTop: 14 }}>
                <button style={S.backBtn} onClick={() => setStep((s) => s - 1)}>← {t.back}</button>
              </div>
            )}

            {!isPaymentStep && cur.type !== "choice" && cur.type !== "file" && cur.type !== "terms" && (
              <div style={S.kbdHint}><kbd style={S.kbd}>Enter</kbd> {t.enterHint}</div>
            )}
          </>
        ) : (
          <div style={S.success}>
            <div style={S.tick}><svg width="26" height="26" fill="none" stroke="#c8f03c" strokeWidth="2.5" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg></div>
            <h2 style={S.successTitle}>{t.successTitle}</h2>
            <p style={S.successSub}>{t.successSub(wsText.title, location)}</p>
            <p style={S.successNote}>📍 {workshop.location ? t.successNoteConfirmed(workshop.location, formatDates(workshop.dates, t)) : t.successNoteTBD}</p>
            <p style={S.successContact}>{t.successContact} <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: "rgba(0,240,255,0.8)" }}>{CONTACT_EMAIL}</a></p>
            <button style={S.successBackBtn} onClick={onClose}>{t.backToWorkshops}</button>
          </div>
        )}

        {legalOpen && <LegalModal type={legalOpen} onClose={() => setLegalOpen(null)} />}
      </div>
    </div>
  );
}

// ── Workshop Card ─────────────────────────────────────────────────
function WorkshopCard({ ws, onApply }) {
  const lang = useLang();
  const t = T[lang];
  const wsText = WS_TEXT[ws.id][lang];
  const [hovered, setHovered] = useState(false);
  const disabled = !ws.enabled;
  const link = `#${workshopHash(ws)}`;

  const handleClick = (e) => {
    if (disabled) { e.preventDefault(); return; }
    onApply(ws);
  };

  return (
    <a
      href={link}
      style={{ textDecoration: "none", color: "inherit", display: "block" }}
      onClick={handleClick}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
    >
      <div style={S.card(hovered, ws.color, disabled)} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
        <div style={S.cardGlow(ws.color)} />
        <div style={S.cardTop}>
          <img src={ws.imageUrl} alt={wsText.title} style={S.cardImg} onError={(e) => { e.target.style.display = "none"; }} />
          {disabled ? <span style={S.comingSoonBadge}>{t.comingSoon}</span> : <span style={S.cardBadge(ws.color)}>{wsText.category}</span>}
        </div>
        <div style={S.cardTitle}>{wsText.title}</div>
        <div style={S.cardDesc}>{wsText.description}</div>
        <div style={S.cardModules}>{wsText.modules.map((m) => <span key={m} style={S.modulePill(ws.color)}>{m}</span>)}</div>
        <div style={S.cardFooter}>
          <div style={S.cardDateWrap}>
            <span style={S.cardDateLabel}>{t.scheduleLabel}</span>
            <span style={S.cardDate(allDatesTBD(ws.dates))}>{formatDates(ws.dates, t)}</span>
            <span style={S.cardLocation}>{ws.location ? `📍 ${ws.location}` : t.tbdNote}</span>
          </div>
          <button style={S.applyBtn(ws.color, disabled)} disabled={disabled} onClick={handleClick}>
            {disabled ? t.notOpenYet : t.apply}
            {!disabled && <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>}
          </button>
        </div>
      </div>
    </a>
  );
}

function LangSwitcher({ lang, setLang }) {
  return (
    <div style={S.langSwitcher}>
      {LANGS.map((l) => (
        <button key={l} style={S.langBtn(l === lang)} onClick={() => setLang(l)}>{LANG_LABEL[l]}</button>
      ))}
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────────
export default function App() {
  const [selected, setSelected] = useState(null);
  const [lang, setLang] = useState("en");
  const t = T[lang];

  const openWorkshop = useCallback((ws) => {
    if (!ws.enabled) return;
    setSelected(ws);
    window.history.replaceState(null, "", `#${workshopHash(ws)}`);
  }, []);

  const closeWorkshop = useCallback(() => {
    setSelected(null);
    window.history.replaceState(null, "", window.location.pathname);
  }, []);

  // Deep-link support: opening a URL with #dhs-ae etc. jumps straight to
  // that workshop's application form (only works for enabled workshops).
  useEffect(() => {
    const hash = window.location.hash.replace("#", "").toLowerCase();
    if (!hash) return;
    const match = WORKSHOPS.find((w) => workshopHash(w) === hash);
    if (match && match.enabled) setSelected(match);
  }, []);

  return (
    <LangContext.Provider value={lang}>
      <div style={S.root}>
        <video style={S.video} autoPlay muted loop playsInline><source src={bg} type="video/mp4" /></video>
        <div style={S.overlay} />
        <div style={S.scanlines} />
        <Particles />
        <div style={S.brand}>D<span style={S.accent}>H</span>S</div>
        <LangSwitcher lang={lang} setLang={setLang} />
        <div style={S.listPage}>
          <div style={S.listHeader}>
            <div style={S.listEyebrow}>{t.eyebrow}</div>
            <h1 style={S.listTitle}>{t.titleLine1}<br /><span style={S.accent}>{t.titleLine2}</span></h1>
            <p style={S.listSub}>{t.subtitle}</p>
            <div style={S.locationNote}>
              <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg>
              {t.locationNote}
            </div>
          </div>
          <div style={S.grid}>{WORKSHOPS.map((ws) => <WorkshopCard key={ws.id} ws={ws} onApply={openWorkshop} />)}</div>
        </div>
        {selected && <FormModal workshop={selected} onClose={closeWorkshop} />}
        <div style={S.footer}>
          Dead Hardware Society · Berlin, Germany · {VENUE}<br />
          <a href={`mailto:${CONTACT_EMAIL}`} style={S.footerLink}>{CONTACT_EMAIL}</a>
        </div>
      </div>
    </LangContext.Provider>
  );
}
